import getDeviceInfo from '@/lib/base/getDeviceInfo';
import {removeLocalData, saveLocalData} from '@/lib/base/localData';
import Message from '@/lib/base/message';
import store from '@/store/index.js';
import i18n from '@/i18n/index.js';
import userService from '@/services/user';
import regionCodes from '@/config/regionCodes';
import url from '@/lib/api/url';
import localeConfig from '@/config/locale';
import {LANGUAGE_LABELS} from '@/constant/language';
import {getLocale} from '@/i18n/utils.js';
import {stage} from '@/constant/env.js';

const locale = getLocale();

const state = {
  cdn: url.cdn,
  viewMode: '',
  device: '',
  orientation: '',
  width: 0,
  height: 0,
  platform: 'web',
  isLoading: false,
  isReviewGameLoading: false,
  isSwitchCourseSheetShow: false,
  isSidebarShow: false,
  isModalPostQuestionShow: false,
  isShowVipModal: false,
  hasVerificationNotification: false,
  isCheatMode: false,
  locale,
  localeName: LANGUAGE_LABELS[locale],
  isChangeLocaleModalShow: false,
  isCreatePvpModalShow: false,
  isJoinPvpModalShow: false,
  isSelectPvpActionModalShow: false,
  localeList: localeConfig,
  previewVideoUrl: '',
  /**
   * MGM 邀請碼領獎完成後要彈出的「獎勵 modal」資料。
   * 設為 { months, days } 時觸發顯示；null 則隱藏。
   * 跨頁存活，但會被 ModalPrize 路由白名單（rank-test 流程）暫時遮蔽，
   * 等使用者離開 rank-test 路徑後再顯示。
   */
  pendingPrize: null,
};

const getters = {
  isLoading(state) {
    return state.isLoading;
  },
  isReviewGameLoading(state) {
    return state.isReviewGameLoading;
  },
  isShowVipModal(state) {
    return state.isShowVipModal;
  },
  pendingPrize(state) {
    return state.pendingPrize;
  },
  viewMode(state) {
    return state.viewMode;
  },
  device(state) {
    return state.device;
  },
  permission() {
    const coursePlan = store.getters['course/currentPlan'];
    if (!coursePlan) {
      return {
        course: false,
        verification: false,
        tsume: false,
        aiGame: false,
      };
    }
    return {
      course: true,
      verification: true,
      tsume: coursePlan.specialTsume?.length > 0,
      aiGame: coursePlan.aiGame,
    };
  },
  regionCodes() {
    const local = state.locale;
    return regionCodes.sort((a, b) => {
      return (a.sort[local] || 9999) - (b.sort[local] || 9999);
    });
  },
  isIos(state) {
    return state.platform === 'ios';
  },
  isBeta() {
    return stage === 'beta';
  },
  isAndroid(state) {
    return state.platform === 'android';
  },
  isChinese(state) {
    return state.locale?.includes('zh');
  },
  isEnglish(state) {
    return state.locale?.includes('en');
  },
};

const actions = {
  openModalPostQuestion({commit}) {
    commit('openModalPostQuestion');
  },
  closeModalPostQuestion({commit}) {
    commit('closeModalPostQuestion');
  },
  openChangeLocaleModal({commit}) {
    commit('openChangeLocaleModal');
  },
  closeChangeLocaleModal({commit}) {
    commit('closeChangeLocaleModal');
  },
  openPreviewModal({commit}, {videoUrl}) {
    commit('openPreviewModal', {videoUrl});
  },
  closePreviewModal({commit}) {
    commit('closePreviewModal');
  },
  async changeLocale({commit}, locale) {
    if (store.getters['user/isLogin']) {
      userService.updateUser({language: locale});
    }

    removeLocalData('videoTextTrack'); // 影片字幕語言
    removeLocalData('videoAudioTrack'); // 影片語音語言
    commit('changeLocale', locale);
  },
  async initLocale({commit}, locale) {
    commit('changeLocale', locale);
    if (store.getters['user/isLogin']) {
      await userService.updateUser({language: locale});
    }
  },
  init({commit}) {
    const data = getDeviceInfo();
    commit('setEnv', data);
  },
  changePlanInit({rootGetters}, plan) {
    const currentPlan = rootGetters['course/currentPlan'];
    if (currentPlan.id === plan.id) {
      return;
    }

    saveLocalData('plan-info', JSON.stringify(plan));
    store.commit('course/setCurrentPlan', plan);
    store.dispatch('tsumeGame/getPracticeTsumeList', plan.id);
    store.dispatch('course/getCourses', plan.id);
    Message.success(i18n.t('已切換課程'), 3000);
  },
};

const mutations = {
  openModalPostQuestion(state) {
    state.isModalPostQuestionShow = true;
  },
  closeModalPostQuestion(state) {
    state.isModalPostQuestionShow = false;
  },
  openChangeLocaleModal(state) {
    state.isChangeLocaleModalShow = true;
  },
  closeChangeLocaleModal(state) {
    state.isChangeLocaleModalShow = false;
  },
  openPreviewModal(state, {videoUrl}) {
    state.previewVideoUrl = videoUrl;
  },
  closePreviewModal(state) {
    state.previewVideoUrl = '';
  },
  setPendingPrize(state, payload) {
    // payload: { months, days }
    state.pendingPrize = payload || null;
  },
  clearPendingPrize(state) {
    state.pendingPrize = null;
  },
  changeLocale(state, locale) {
    if (!locale) return;
    let isSameLocale = false;
    if (state.locale === locale) {
      isSameLocale = true;
    }
    i18n.locale = locale;
    state.locale = locale;
    state.localeName = state.localeList.find(
      (item) => item.code === locale
    ).name;
    saveLocalData('locale', locale);
    if (!isSameLocale) {
      window.location.href = `/#/${locale}`;
    }
  },
  setEnv(state, data) {
    const {viewMode, device, orientation, height, width} = data;
    state.viewMode = viewMode;
    state.device = device;
    state.orientation = orientation;
    state.width = width;
    state.height = height;
  },
  setWindowSize(state, data) {
    const {orientation, height, width} = data;
    state.orientation = orientation;
    state.width = width;
    state.height = height;
  },
  setIsLoading(state, value) {
    state.isLoading = value;
  },
  setIsReviewGameLoading(state, value) {
    state.isReviewGameLoading = value;
  },
  setIsSwitchCourseSheetShow(state, value) {
    state.isSwitchCourseSheetShow = value;
  },
  setIsSidebarShow(state, value) {
    state.isSidebarShow = value;
  },
  setHasVerificationNotification(state, value) {
    state.hasVerificationNotification = value;
  },
  toggleCheatMode(state) {
    state.isCheatMode = !state.isCheatMode;
  },
  setIsShowVipModal(state, value) {
    state.isShowVipModal = value;
  },
  setPlatform(state, value) {
    state.platform = value;
  },
  setIsCreatePvpModalShow(state, value) {
    if (value) state.isJoinPvpModalShow = false;
    state.isCreatePvpModalShow = value;
  },
  setIsJoinPvpModalShow(state, value) {
    if (value) state.isCreatePvpModalShow = false;
    state.isJoinPvpModalShow = value;
  },
  setIsSelectPvpActionModalShow(state, value) {
    state.isCreatePvpModalShow = false;
    state.isJoinPvpModalShow = false;
    state.isSelectPvpActionModalShow = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
